import { useQuery } from '@tanstack/react-query';
import { Check, Minus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Badge, StatusBadge, Tooltip } from '@/components/ui/misc';
import { entityKindOf, loadEntity, loadEntityList, type EntityKind } from '@/lib/entities';
import * as fmt from '@/lib/format';
import { cn, humanize, isUuid } from '@/lib/utils';

/** Resolve an id to a readable name (cached, deduplicated). */
export function useEntity(kind: EntityKind | null, id: string | null | undefined) {
  const list = useQuery({
    queryKey: ['entity-list', kind?.kind],
    queryFn: () => loadEntityList(kind!),
    enabled: Boolean(kind && id && kind.search === 'client'),
    staleTime: 5 * 60_000,
  });
  const one = useQuery({
    queryKey: ['entity', kind?.kind, id],
    queryFn: () => loadEntity(kind!, id!),
    enabled: Boolean(kind && id && kind.search === 'server'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (!kind || !id) return { row: null, loading: false };
  if (kind.search === 'client') return { row: list.data?.find((r) => r.id === id) ?? null, loading: list.isLoading };
  return { row: one.data ?? null, loading: one.isLoading };
}

/** `link={false}` when the reference sits inside another link (a row that is itself clickable). */
export function EntityRef({ kind, id, withAvatar = true, link = true, className }: { kind: EntityKind; id: string | null | undefined; withAvatar?: boolean; link?: boolean; className?: string }) {
  const { row, loading } = useEntity(kind, id);
  if (!id) return <span className="text-muted-foreground">—</span>;
  if (loading) return <span className="skeleton inline-block h-4 w-24 align-middle" />;
  if (!row) return <span className="font-mono text-xs text-muted-foreground">{id.slice(0, 8)}</span>;
  const label = kind.label(row) || id.slice(0, 8);
  const content = (
    <span className={cn('inline-flex max-w-full items-center gap-2 align-middle', className)}>
      {withAvatar && kind.kind === 'person' && <Avatar size={22} src={kind.image?.(row)} first={String(row.firstName ?? '')} last={String(row.lastName ?? '')} />}
      {withAvatar && kind.kind === 'horse' && kind.image?.(row) && <img src={kind.image(row)} alt="" className="size-[22px] rounded-md object-cover" />}
      <span className="truncate">{label}</span>
    </span>
  );
  return kind.href && link ? (
    <Link to={kind.href(id)} onClick={(e) => e.stopPropagation()} className="hover:text-primary hover:underline">
      {content}
    </Link>
  ) : (
    content
  );
}

const MONEY_KEYS = /(amount|price|total|subtotal|discount|vat(Amount)?|refundedAmount|fee|mrr|gross|net|refunds|revenue|balance|ltv|lifetimeValue|unitCost)$/i;
const DATE_ONLY = /(^date$|Date$|^validFrom$|^validUntil$|dateOfBirth|arrivedAt$|leftAt$|fromDate|toDate|occurrenceDate|startDate|endDate)/;
const PERCENT_KEYS = /(Pct|Rate|percent)$/;

/**
 * Smart renderer for any API value, chosen by its key: money in minor units, dates in the stable's zone,
 * statuses as badges, ids as linked names, media as thumbnails, booleans as ticks.
 */
export function Value({ k, v, context = '', row }: { k: string; v: unknown; context?: string; row?: Record<string, unknown> }): ReactNode {
  if (v === null || v === undefined || v === '') return <span className="text-muted-foreground/70">—</span>;
  const currency = (row?.currency as string | undefined) ?? undefined;
  if (typeof v === 'boolean') return v ? <Check className="size-4 text-success" /> : <Minus className="size-4 text-muted-foreground/60" />;
  if (k === 'status' || k === 'attendance' || k === 'safetyLevel' || k === 'gate' || (k === 'result' && typeof v === 'string' && v.length < 16)) return <StatusBadge value={String(v)} />;
  if (typeof v === 'number') {
    if (MONEY_KEYS.test(k) && !/Pct|Count|Days|Qty/i.test(k)) return <span className="tabular">{fmt.money(v, currency)}</span>;
    if (PERCENT_KEYS.test(k)) return <span className="tabular">{fmt.percent(v, 1)}</span>;
    if (/^duration(S)?$|S$/.test(k) && /duration|walk|trot|canter|rein/i.test(k)) return <span className="tabular">{fmt.duration(v)}</span>;
    return <span className="tabular">{fmt.number(v, 2)}</span>;
  }
  if (typeof v === 'string') {
    const uuid = isUuid(v as unknown);
    const kind = uuid ? entityKindOf(k, context) : null;
    if (kind) return <EntityRef kind={kind} id={v} />;
    if (uuid) return <span className="font-mono text-xs text-muted-foreground">{v.slice(0, 8)}</span>;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v) || (DATE_ONLY.test(k) && !/T\d/.test(v))) return <span className="tabular">{fmt.date(v)}</span>;
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return (
      <Tooltip content={fmt.dateTime(v, { weekday: 'long' })}>
        <span className="tabular">{fmt.dateTime(v)}</span>
      </Tooltip>
    );
    if (/^https?:\/\//.test(v)) return (
      <a href={v} target="_blank" rel="noreferrer" className="text-primary hover:underline">
        {v.length > 48 ? `${v.slice(0, 45)}…` : v}
      </a>
    );
    if (['type', 'kind', 'role', 'channel', 'method', 'source', 'period', 'category', 'trigger', 'paidWith', 'ownership', 'sex', 'severity', 'riderLevel', 'weightBand', 'minLevel', 'preferredLanguage', 'gender', 'scope', 'visibility'].includes(k)) {
      return <Badge>{v.replace(/_/g, ' ')}</Badge>;
    }
    return <span className="whitespace-pre-line">{v.length > 160 ? `${v.slice(0, 157)}…` : v}</span>;
  }
  if (Array.isArray(v)) {
    if (!v.length) return <span className="text-muted-foreground/70">—</span>;
    if (v.every((x) => typeof x === 'string' && isUuid(x))) {
      const kind = entityKindOf(k, context);
      if (kind) return (
        <span className="flex flex-wrap gap-1.5">
          {(v as string[]).map((id) => (
            <Badge key={id}>
              <EntityRef kind={kind} id={id} withAvatar={false} />
            </Badge>
          ))}
        </span>
      );
    }
    if (v.every((x) => typeof x !== 'object')) return (
      <span className="flex flex-wrap gap-1">
        {v.map((x, i) => (
          <Badge key={i}>{String(x).replace(/_/g, ' ')}</Badge>
        ))}
      </span>
    );
    return <span className="text-muted-foreground">{v.length} items</span>;
  }
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('amount' in o && 'currency' in o) return <span className="tabular">{fmt.money(o.amount as number, o.currency as string)}</span>;
    if ('thumbUrl' in o || ('url' in o && 'mime' in o)) return <img src={(o.thumbUrl as string) || (o.url as string)} alt="" className="h-10 w-14 rounded-md object-cover ring-1 ring-border" />;
    if ('firstName' in o) return <span>{`${o.firstName ?? ''} ${o.lastName ?? ''}`}</span>;
    if ('name' in o) return <span>{String(o.name)}</span>;
    if ('titleEn' in o) return <span>{String(o.titleEn)}</span>;
    const entries = Object.entries(o);
    if (entries.length && entries.length <= 6 && entries.every(([, x]) => x === null || ['string', 'number', 'boolean'].includes(typeof x))) {
      // Rewards and similar small objects: "Wallet credit · AED 100". Ride credits are counts, not money.
      const countable = typeof o.type === 'string' && /ride_credit|credits?$/.test(o.type) && !/wallet/.test(o.type);
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {entries.map(([k, x], i) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              {i > 0 && <span className="text-muted-foreground/50">·</span>}
              {k !== 'type' && <span className="text-muted-foreground">{humanize(k)}</span>}
              {k === 'amount' && countable ? <span className="tabular">{fmt.number(x as number)}</span> : <Value k={k} v={x} context={context} row={o} />}
            </span>
          ))}
        </span>
      );
    }
    return <code className="block max-w-[28rem] truncate rounded bg-muted px-1.5 py-0.5 text-[11.5px]">{JSON.stringify(v)}</code>;
  }
  return String(v);
}
